import { useEffect, useRef } from 'react';

/**
 * Drives the personalize page's cover → inside2 auto-flip.
 *
 * The flip is gated on the COVER IMAGE being loaded (`coverLoaded`), not just the card DATA
 * (`loading`) — otherwise the 2s timer can fire while the cover image is still downloading and the
 * page turns before the shopper ever sees the cover (the reported bug). A separate safety timeout
 * releases the gate if the cover never fires load (broken/slow image), so the flip can't hang forever.
 *
 * Callbacks are kept in refs so passing fresh inline functions each render doesn't restart the timers.
 *
 * @param {boolean}  loading              card DATA still loading
 * @param {object}   card                 the loaded card (falsy until present)
 * @param {boolean}  coverLoaded          the cover IMAGE has loaded (or errored)
 * @param {string}   currentView          'cover' | 'inside1' | 'inside2' | 'back'
 * @param {boolean}  hasAutoTransitioned  the flip already happened once
 * @param {Function} onCoverLoadTimeout   called when the safety timeout elapses (release the gate)
 * @param {Function} onFlip               called to perform the cover → inside2 flip
 * @param {number}   [flipDelayMs=2000]   how long the cover is held before flipping
 * @param {number}   [safetyDelayMs=6000] max wait for the cover before proceeding anyway
 */
export function useAutoFlipToInside2({
  loading,
  card,
  coverLoaded,
  currentView,
  hasAutoTransitioned,
  onCoverLoadTimeout,
  onFlip,
  flipDelayMs = 2000,
  safetyDelayMs = 6000,
}) {
  const onFlipRef = useRef(onFlip);
  const onTimeoutRef = useRef(onCoverLoadTimeout);
  useEffect(() => {
    onFlipRef.current = onFlip;
    onTimeoutRef.current = onCoverLoadTimeout;
  });

  // Safety net: don't block the flip forever if the cover never loads.
  useEffect(() => {
    if (loading || !card || coverLoaded || hasAutoTransitioned) return undefined;
    const safety = setTimeout(() => onTimeoutRef.current(), safetyDelayMs);
    return () => clearTimeout(safety);
  }, [loading, card, coverLoaded, hasAutoTransitioned, safetyDelayMs]);

  // The flip itself — only once the cover IMAGE is loaded and we're still on the cover view.
  useEffect(() => {
    if (loading || !card || !coverLoaded || currentView !== 'cover' || hasAutoTransitioned) {
      return undefined;
    }
    const timer = setTimeout(() => onFlipRef.current(), flipDelayMs);
    return () => clearTimeout(timer);
  }, [loading, card, coverLoaded, currentView, hasAutoTransitioned, flipDelayMs]);
}

export default useAutoFlipToInside2;
